import {useIntl} from 'react-intl';
import {Box, Flex, FlexProps, Text} from '@chakra-ui/react';

import {useTranslate} from '@eksab/i18n';
import {useRegionSettings} from '@eksab/hooks';
import {CoinsIcon} from '@eksab/assets/icons';

import {MiniBadgeView} from '@eksab/features/store/badges/components';

import type {PaymentProps} from './Payment';
import {DiscountVoucher} from '../hooks';

const defaultSummaryItemStyles: FlexProps = {gap: 4, align: 'center', justify: 'space-between', px: 6, fontSize: 'md'};

type OrderSummaryProps = PaymentProps & {
  voucher?: DiscountVoucher;
};

export function OrderSummary({pack, type, voucher}: OrderSummaryProps) {
  const format = useIntl().formatNumber;

  const t = useTranslate();
  const {data: regionSettings} = useRegionSettings();

  const formatPrice = (price: number) => `${format(price)} ${regionSettings?.currency}`;

  return (
    <Flex as="section" flexDir="column" gap="3.5">
      <Text as="h2" fontSize={['sm', null, 'md']} fontWeight="700">
        {t('payment.order.summary')}
      </Text>

      {type === 'badge' && <MiniBadgeView badge={pack.badge} discount={pack.price.discount} mb="1" />}

      <Flex flexDir="column" gap="4" bg="#f7f7f7" borderWidth="1px" borderColor="green.400" rounded="0.875rem">
        <Flex {...defaultSummaryItemStyles} bg="white" py="5" mb="1" roundedTop="inherit">
          <Text fontWeight="600">{t(`payment.order${type === 'badge' ? '.benefits' : ''}`)}</Text>
          <Text fontWeight="700" fontSize="md" display="flex" alignItems="center" gap="2.5">
            <CoinsIcon h="6" w="auto" />
            {format(pack.coins)}
          </Text>
        </Flex>

        <Flex {...defaultSummaryItemStyles}>
          <Text fontWeight="500">{t('payment.order.price')}</Text>
          <Text fontWeight="700">{formatPrice(pack.price.original)}</Text>
        </Flex>

        {!!pack.price.discount && (
          <Flex {...defaultSummaryItemStyles}>
            <Text fontWeight="500">{t('payment.order.discount')}</Text>
            <Text fontWeight="700">{formatPrice(pack.price.discounted - pack.price.original)}</Text>
          </Flex>
        )}

        {!!voucher && (
          <Flex {...defaultSummaryItemStyles}>
            <Text fontWeight="500">{t('payment.order.voucher')}</Text>
            <Text fontWeight="700">-{formatPrice(voucher.discount_amount)}</Text>
          </Flex>
        )}

        <Flex {...defaultSummaryItemStyles}>
          <Text fontWeight="500">
            {t('app.tax.vat')} ({format(pack.price.vat.percentage, {style: 'percent'})})
          </Text>
          <Text fontWeight="700">+{formatPrice(voucher?.order.vat ?? pack.price.vat.value)}</Text>
        </Flex>

        <Box h="1px" mx="6" bg="blackAlpha.300" mt="2.5" mb="1.5" />

        <Flex {...defaultSummaryItemStyles} fontWeight="700" pb="6">
          <Text>{t('payment.order.total')}</Text>
          <Text>{formatPrice(voucher?.order.total ?? pack.price.total)}</Text>
        </Flex>
      </Flex>
    </Flex>
  );
}
